/**
 * /_gemini — Public showcase landing for the SalesFlow demo.
 *
 * Anyone visiting this URL can hit "Launch live demo" and is auto-logged
 * into a read-mostly demo tenant pre-loaded with a quarter's worth of
 * customers, sites, leads, job-cards, sales documents, candidate CVs,
 * inventory, calendar events and chat threads. Writes are blocked by
 * the backend's demo_guard middleware.
 *
 * This is also the eventual home of the super-admin console (per the
 * PRD roadmap P2). For now it's the demo entry point only.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Rocket, ShieldCheck, ArrowRight, Loader2, Eye,
  Users, Briefcase, FileText, MessageSquare, Calendar, Package,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STAT_ICONS = {
  customers: Users,
  jobcards: Briefcase,
  sales_documents: FileText,
  chat_messages: MessageSquare,
  calendar_events: Calendar,
  inventory: Package,
  leads: Sparkles,
  candidate_cvs: Users,
};

const STAT_LABELS = {
  customers: "Customers",
  sites: "Sites",
  leads: "Leads",
  jobcards: "Job-cards",
  sales_documents: "Sales docs",
  inventory: "Inventory items",
  candidate_cvs: "Candidate CVs",
  calendar_events: "Calendar events",
  chats: "Chat threads",
  chat_messages: "Messages",
};

export default function GeminiLanding() {
  const nav = useNavigate();
  const { demoLogin } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/demo/info");
        setInfo(data);
      } catch {
        setInfo({ seeded: false });
      }
    })();
  }, []);

  const handleLaunch = async () => {
    setBusy(true);
    setErr("");
    try {
      await demoLogin();
      nav("/home");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't start the demo. Try again in a moment.");
      setBusy(false);
    }
  };

  const counts = info?.counts || {};
  const visibleStats = ["customers", "leads", "jobcards", "sales_documents", "candidate_cvs", "inventory", "calendar_events", "chat_messages"]
    .filter((k) => counts[k] > 0);

  return (
    <div className="min-h-screen bg-[#0b0f14] text-white overflow-x-hidden" data-testid="gemini-landing">
      {/* hero */}
      <section className="relative px-6 sm:px-10 lg:px-16 pt-12 sm:pt-20 pb-10">
        <div
          className="pointer-events-none absolute -top-40 -right-20 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #1FB6C1 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #9333EA 0%, transparent 70%)" }}
        />
        <div className="relative max-w-5xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-xs text-[#1FB6C1] mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Rogue · live demo workspace
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-4">
            Field service, sales & ops —
            <br />
            <span className="text-[#1FB6C1]">running in one brain.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#9aa3b5] max-w-2xl mb-8 leading-relaxed">
            A real, working tenant pre-loaded with 15 customers, 80 leads and 120 job-cards
            in every phase. Click below to log in instantly — no signup, no questions asked.
            Writes are disabled so you can explore freely without breaking anything.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-10">
            <button
              type="button"
              onClick={handleLaunch}
              disabled={busy || info?.seeded === false}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#1FB6C1] hover:bg-[#19a3ad] text-[#06222a] font-semibold text-base disabled:opacity-50 transition-colors"
              data-testid="gemini-launch-demo"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              {busy ? "Launching…" : "Launch live demo"}
              {!busy && <ArrowRight className="w-4 h-4" />}
            </button>
            <a
              href="https://salesflow.hidroverse.co.za"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm transition-colors"
              data-testid="gemini-production-link"
            >
              <Eye className="w-4 h-4" /> Go to production
            </a>
            <div className="inline-flex items-center gap-1.5 text-xs text-[#647087] ml-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Read-only · resets nightly
            </div>
          </div>

          {err && (
            <div className="text-rose-300 text-sm mb-6 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2 max-w-xl">
              {err}
            </div>
          )}

          {info?.seeded === false && (
            <div className="text-amber-300 text-sm mb-6 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2 max-w-xl">
              Demo hasn't been seeded yet. Admin: run <code className="text-xs">python -m scripts.seed_demo_tenant</code>.
            </div>
          )}
        </div>
      </section>

      {/* live stats — what's in the demo */}
      {visibleStats.length > 0 && (
        <section className="px-6 sm:px-10 lg:px-16 pb-12 max-w-6xl" data-testid="gemini-stats">
          <h2 className="text-base text-[#9aa3b5] uppercase tracking-[0.2em] mb-5">What's loaded in the demo</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {visibleStats.map((k) => {
              const Icon = STAT_ICONS[k] || Sparkles;
              return (
                <div
                  key={k}
                  className="hv-card p-4 border border-white/5 hover:border-[#1FB6C1]/30 transition-colors"
                >
                  <Icon className="w-5 h-5 text-[#1FB6C1] mb-2" />
                  <div className="text-2xl font-bold tabular-nums">{counts[k]}</div>
                  <div className="text-xs text-[#9aa3b5] mt-0.5">{STAT_LABELS[k] || k}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* feature showcase */}
      <section className="px-6 sm:px-10 lg:px-16 pb-16 max-w-6xl">
        <h2 className="text-base text-[#9aa3b5] uppercase tracking-[0.2em] mb-6">Try these flows first</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              icon: Briefcase,
              title: "Job-card Kanban",
              body: "120 jobs in every operational phase — from picking through invoice-sent. Drag-reorder tasks, see per-task photos, jump to chats.",
            },
            {
              icon: FileText,
              title: "OCR Receipt scan",
              body: "Drop a receipt photo on Home → 'Receipt scan'. The backend OCR pipeline matches lines, dedupes via SHA-256, and stages procurement.",
            },
            {
              icon: Users,
              title: "Recruitment CV Brain",
              body: "30 candidate CVs pre-analysed with Hidro's standalone hireability score, SA-market compensation band, top strengths/concerns and best-fit roles.",
            },
            {
              icon: MessageSquare,
              title: "Hidro AI Co-pilot",
              body: "Tap the bottom-right Sparkles. Ask Hidro about hot leads, picking shortages, today's tech routes — it queries the live tenant in real time.",
            },
            {
              icon: Calendar,
              title: "Smart Calendar",
              body: "80 events across site visits, installs, deliveries and training. Filterable by tech, customer and date.",
            },
            {
              icon: Package,
              title: "Procurement planner",
              body: "Sees picking demand + scheduled outstandings in one view. Folds in supplier preferences and reorder thresholds.",
            },
          ].map((card) => (
            <div
              key={card.title}
              className="hv-card p-5 border border-white/5 hover:border-[#1FB6C1]/30 transition-colors"
            >
              <card.icon className="w-5 h-5 text-[#1FB6C1] mb-3" />
              <div className="text-base font-semibold mb-1">{card.title}</div>
              <div className="text-sm text-[#9aa3b5] leading-relaxed">{card.body}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-6 sm:px-10 lg:px-16 py-8 border-t border-white/5 text-xs text-[#647087] max-w-6xl">
        Powered by Gemini Space · all data shown is fabricated demo data · for production white-label
        <a href="mailto:hello@geminii-ai.com" className="text-[#1FB6C1] hover:underline ml-1">get in touch</a>
      </footer>
    </div>
  );
}
